import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  inject,
  input,
  output,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { connectState } from '@examdojo/angular/util';
import { ButtonComponent } from '@examdojo/core/button';
import { IconComponent } from '@examdojo/core/icon';
import { PlatformService } from '@examdojo/platform';
import { TranslocoPipe } from '@jsverse/transloco';
import { ComponentSize } from '@examdojo/core/model';
import { SingleFilterData } from '../multiple-filters-button/filter-section.directive';
import { FiltersPanelDialogService } from '../multiple-filters-button/filters-panel-dialog.service';

@Component({
  selector: 'dojo-filter-button',
  templateUrl: './filter-button.component.html',
  styleUrl: './filter-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatMenuModule, TranslocoPipe, IconComponent, ButtonComponent, NgTemplateOutlet],
})
export class FilterButtonComponent {
  private readonly platformService = inject(PlatformService);
  private readonly dialogService = inject(FiltersPanelDialogService);

  readonly defaultLabel = input.required<string>();
  readonly selectedLabel = input<string>('');
  readonly count = input<number>(0);
  readonly clearLabelKey = input<string>('examdojo.clear');
  readonly size = input<ComponentSize>('m');
  readonly customMatMenuClass = input<string>('');
  readonly alwaysShowCount = input<boolean>(false);
  readonly disabled = input<boolean>(false);

  readonly clear = output<void>();

  readonly menuTrigger = viewChild<MatMenuTrigger>('menuTrigger');
  readonly contentTemplate = contentChild<TemplateRef<unknown>>('filterContent');

  readonly isSelected = computed(() => this.count() > 0);
  readonly showCount = computed(() => (this.alwaysShowCount() ? this.count() > 0 : this.count() > 1));

  readonly displayLabel = computed(() => {
    const count = this.count();
    return count === 1 ? this.selectedLabel() || this.defaultLabel() : this.defaultLabel();
  });

  readonly state = connectState({
    isSmall: this.platformService.isSmall$,
  });

  onButtonClick(): void {
    if (this.disabled()) {
      return;
    }
    if (this.state.isSmall) {
      this.openMobileDialog();
    }
  }

  onClear(): void {
    this.clear.emit();
    this.menuTrigger()?.closeMenu();
  }

  private openMobileDialog(): void {
    const template = this.contentTemplate();
    if (!template) {
      return;
    }

    const filter: SingleFilterData = {
      label: this.defaultLabel(),
      count: this.count(),
      template,
      clearFn: () => this.clear.emit(),
      isSelected: this.isSelected,
    };

    this.dialogService.openSingleFilter(filter);
  }
}
